import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface SurveyQuestion {
  id: string;
  type: "rating" | "nps" | "yesno" | "multipleChoice" | "text";
  text: string;
  linkedAssumption?: number;
  options?: string[] | null;
  required?: boolean;
}

export interface Survey {
  id?: string;
  title: string;
  description: string;
  questions: SurveyQuestion[];
  settings?: {
    allowAnonymous?: boolean;
    requireEmail?: boolean;
  };
  status?: "draft" | "live" | "closed";
  responseCount?: number;
}

export interface SurveyResponse {
  id: string;
  answers: Record<string, any>;
  respondentEmail?: string;
  source: string;
  submittedAt: any;
}

export interface QuestionStats {
  responseCount: number;
  type: string;
  average?: number;
  distribution?: Record<string, number>;
  nps?: number;
  promoters?: number;
  passives?: number;
  detractors?: number;
  yesPercentage?: number;
  yesCount?: number;
  noCount?: number;
  responses?: string[];
}

export interface ValidationScore {
  questions: Array<{ questionId: string; stats: QuestionStats }>;
  validated: "validated" | "invalidated" | "pending" | null;
}

export interface SurveyStats {
  totalResponses: number;
  byQuestion: Record<string, QuestionStats>;
  validationScores: Record<string, ValidationScore>;
}

interface GenerateSurveyResponse {
  success: boolean;
  survey: {
    title: string;
    description: string;
    questions: SurveyQuestion[];
  };
  assumptions: string[];
}

interface CreateSurveyResponse {
  success: boolean;
  surveyId: string;
  shareUrl: string;
}

interface GetResponsesResponse {
  success: boolean;
  survey: {
    id: string;
    title: string;
    status: string;
    responseCount: number;
  };
  responses: SurveyResponse[];
  stats: SurveyStats;
}

interface UseSurveyBuilderResult {
  isLoading: boolean;
  isGenerating: boolean;
  isPublishing: boolean;
  error: string | null;
  generatedSurvey: Survey | null;
  createdSurveyId: string | null;
  shareUrl: string | null;
  responses: SurveyResponse[];
  stats: SurveyStats | null;
  generateQuestions: (projectId: string, targetStep?: number) => Promise<void>;
  createSurvey: (
    projectId: string,
    survey: Survey
  ) => Promise<CreateSurveyResponse | null>;
  publishSurvey: (projectId: string, surveyId: string) => Promise<void>;
  getResponses: (projectId: string, surveyId: string) => Promise<void>;
  updateQuestion: (questionId: string, updates: Partial<SurveyQuestion>) => void;
  addQuestion: (question: SurveyQuestion) => void;
  removeQuestion: (questionId: string) => void;
  reorderQuestions: (questions: SurveyQuestion[]) => void;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for building and managing validation surveys
 */
export function useSurveyBuilder(): UseSurveyBuilderResult {
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedSurvey, setGeneratedSurvey] = useState<Survey | null>(null);
  const [createdSurveyId, setCreatedSurveyId] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [stats, setStats] = useState<SurveyStats | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setGeneratedSurvey(null);
    setCreatedSurveyId(null);
    setShareUrl(null);
    setResponses([]);
    setStats(null);
  }, []);

  const generateQuestions = useCallback(
    async (projectId: string, targetStep: number = 20): Promise<void> => {
      setIsGenerating(true);
      setError(null);

      try {
        const functions = getFunctions();
        const generateFunction = httpsCallable<
          { projectId: string; targetStep: number },
          GenerateSurveyResponse
        >(functions, "generateSurveyQuestions");

        const result = await generateFunction({ projectId, targetStep });

        setGeneratedSurvey({
          title: result.data.survey.title,
          description: result.data.survey.description,
          questions: result.data.survey.questions,
          settings: {
            allowAnonymous: true,
            requireEmail: false,
          },
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate survey questions";
        setError(errorMessage);
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  const createSurvey = useCallback(
    async (
      projectId: string,
      survey: Survey
    ): Promise<CreateSurveyResponse | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const functions = getFunctions();
        const createFunction = httpsCallable<
          {
            projectId: string;
            title: string;
            description: string;
            questions: SurveyQuestion[];
            settings: any;
          },
          CreateSurveyResponse
        >(functions, "createSurvey");

        const result = await createFunction({
          projectId,
          title: survey.title,
          description: survey.description,
          questions: survey.questions,
          settings: survey.settings,
        });

        setCreatedSurveyId(result.data.surveyId);
        setShareUrl(result.data.shareUrl);

        return result.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create survey";
        setError(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const publishSurvey = useCallback(
    async (projectId: string, surveyId: string): Promise<void> => {
      setIsPublishing(true);
      setError(null);

      try {
        const functions = getFunctions();
        const publishFunction = httpsCallable<
          { projectId: string; surveyId: string },
          { success: boolean; shareUrl: string }
        >(functions, "publishSurvey");

        const result = await publishFunction({ projectId, surveyId });
        setShareUrl(result.data.shareUrl);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to publish survey";
        setError(errorMessage);
      } finally {
        setIsPublishing(false);
      }
    },
    []
  );

  const getResponses = useCallback(
    async (projectId: string, surveyId: string): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const functions = getFunctions();
        const getFunction = httpsCallable<
          { projectId: string; surveyId: string },
          GetResponsesResponse
        >(functions, "getSurveyResponses");

        const result = await getFunction({ projectId, surveyId });

        setResponses(result.data.responses);
        setStats(result.data.stats);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch responses";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const updateQuestion = useCallback(
    (questionId: string, updates: Partial<SurveyQuestion>): void => {
      if (!generatedSurvey) return;

      setGeneratedSurvey({
        ...generatedSurvey,
        questions: generatedSurvey.questions.map((q) =>
          q.id === questionId ? { ...q, ...updates } : q
        ),
      });
    },
    [generatedSurvey]
  );

  const addQuestion = useCallback(
    (question: SurveyQuestion): void => {
      if (!generatedSurvey) return;

      setGeneratedSurvey({
        ...generatedSurvey,
        questions: [...generatedSurvey.questions, question],
      });
    },
    [generatedSurvey]
  );

  const removeQuestion = useCallback(
    (questionId: string): void => {
      if (!generatedSurvey) return;

      setGeneratedSurvey({
        ...generatedSurvey,
        questions: generatedSurvey.questions.filter((q) => q.id !== questionId),
      });
    },
    [generatedSurvey]
  );

  const reorderQuestions = useCallback(
    (questions: SurveyQuestion[]): void => {
      if (!generatedSurvey) return;

      setGeneratedSurvey({
        ...generatedSurvey,
        questions,
      });
    },
    [generatedSurvey]
  );

  return {
    isLoading,
    isGenerating,
    isPublishing,
    error,
    generatedSurvey,
    createdSurveyId,
    shareUrl,
    responses,
    stats,
    generateQuestions,
    createSurvey,
    publishSurvey,
    getResponses,
    updateQuestion,
    addQuestion,
    removeQuestion,
    reorderQuestions,
    clearError,
    reset,
  };
}
